import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Award, Trash2, Users, TrendingUp, BarChart3, Plus, Edit, Save, X } from 'lucide-react';
import axios from 'axios';
import { useAuth, API } from '../../App';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';

const adminInputClass =
  "w-full bg-misel-card border border-white/15 text-white placeholder:text-gray-500 rounded-sm px-3 py-2 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/40 disabled:opacity-60 disabled:cursor-not-allowed";
const adminTextareaClass =
  "w-full min-h-[90px] bg-misel-card border border-white/15 text-white placeholder:text-gray-500 rounded-sm px-3 py-2 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/40 disabled:opacity-60 disabled:cursor-not-allowed";
const adminSelectTriggerClass =
  "w-full bg-misel-card border border-white/15 text-white rounded-sm px-3 py-2 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/40 disabled:opacity-60 disabled:cursor-not-allowed data-[placeholder]:text-gray-500";
const adminSelectContentClass =
  "z-[9999] bg-[#111111] border border-white/15 text-white shadow-xl";
const adminSelectItemClass =
  "text-white focus:bg-primary/20 focus:text-white";

const TRIGGERS = [
  'videos_watched',
  'video_completed',
  'watch_minutes',
  'category_completed',
  'referrals',
  'points_earned',
  'manual'
];

const REWARD_TYPES = [
  'none',
  'points',
  'discount_percent',
  'discount_amount',
  'free_access'
];

const emptyBadge = () => ({
  slug: '',
  name_el: '',
  name_en: '',
  description_el: '',
  description_en: '',
  icon: 'award',
  color: '#b91c1c',
  trigger: 'manual',
  trigger_value: '',
  reward_type: 'none',
  reward_value: '',
  points_reward: '',
  is_active: true
});

const BadgesManagement = () => {
  const { i18n } = useTranslation();
  const { token } = useAuth();
  const el = i18n.language === 'el';
  const [badges, setBadges] = useState([]);
  const [gamStats, setGamStats] = useState(null);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyBadge());
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      setError(null);
      const [bRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/badges`, { headers }),
        axios.get(`${API}/admin/gamification/stats`, { headers })
      ]);
      setBadges(bRes.data);
      setGamStats(sRes.data);
    } catch (e) {
      console.error(e);
      const message = e.response?.data?.detail || (el ? 'Σφάλμα φόρτωσης badges.' : 'Failed to load badges.');
      setError(message);
      toast.error(message);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyBadge());
    setShowForm(true);
  };

  const openEdit = (badge) => {
    setEditingId(badge.id);
    setForm({
      slug: badge.slug || '',
      name_el: badge.name_el || badge.name || '',
      name_en: badge.name_en || badge.name || '',
      description_el: badge.description_el || '',
      description_en: badge.description_en || '',
      icon: badge.icon || 'award',
      color: badge.color || '#b91c1c',
      trigger: badge.trigger || 'manual',
      trigger_value: badge.trigger_value ?? '',
      reward_type: badge.reward_type || 'none',
      reward_value: badge.reward_value ?? '',
      points_reward: badge.points_reward ?? '',
      is_active: badge.is_active !== false
    });
    setShowForm(true);
  };

  const badgePayload = () => ({
    ...form,
    trigger_value: form.trigger === 'manual' || form.trigger_value === '' ? null : Number(form.trigger_value),
    reward_value: form.reward_value === '' ? null : Number(form.reward_value),
    points_reward: form.points_reward === '' ? null : Number(form.points_reward)
  });

  const saveBadge = async (e) => {
    e.preventDefault();
    if (!form.name_el && !form.name_en) {
      toast.error(el ? 'Συμπλήρωσε ελληνικό ή αγγλικό τίτλο.' : 'Add a Greek or English title.');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/badges/${editingId}`, badgePayload(), { headers });
        toast.success(el ? 'Το badge ενημερώθηκε' : 'Badge updated');
      } else {
        await axios.post(`${API}/admin/badges`, badgePayload(), { headers });
        toast.success(el ? 'Το badge δημιουργήθηκε' : 'Badge created');
      }
      setShowForm(false);
      setEditingId(null);
      setForm(emptyBadge());
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || (el ? 'Αποτυχία αποθήκευσης badge.' : 'Failed to save badge.'));
    } finally {
      setSaving(false);
    }
  };

  const toggleBadge = async (id, current) => {
    try {
      await axios.put(`${API}/admin/badges/${id}?is_active=${!current}`, {}, { headers });
      toast.success(el ? 'Ενημερώθηκε' : 'Updated');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || (el ? 'Δεν είναι διαθέσιμη η ενημέρωση badge.' : 'Badge update is not available.'));
    }
  };

  const archiveBadge = async (id) => {
    if (!window.confirm(el ? 'Αρχειοθέτηση badge;' : 'Archive badge?')) return;
    try {
      await axios.delete(`${API}/admin/badges/${id}`, { headers });
      toast.success(el ? 'Αρχειοθετήθηκε' : 'Archived');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || (el ? 'Δεν είναι διαθέσιμη η αρχειοθέτηση badge.' : 'Badge archive is not available.'));
    }
  };

  return (
    <div data-testid="badges-management">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-roboto font-bold text-lg lg:text-2xl text-white">
            {el ? 'Gamification & Badges' : 'Gamification & Badges'}
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {el ? 'Δημιουργία badges, triggers και ανταμοιβών.' : 'Create badges, triggers, and rewards.'}
          </p>
        </div>
        <Button onClick={openCreate} className="bg-primary hover:bg-primary/90">
          <Plus size={16} className="mr-2" />
          {el ? 'Νέο Badge' : 'New Badge'}
        </Button>
      </div>

      {error && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-200 rounded-sm p-4 mb-6 text-sm">
          {error}
        </div>
      )}

      {gamStats && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
          <div className="bg-misel-card border border-white/10 rounded-sm p-5">
            <div className="flex items-center gap-3 mb-2">
              <Award size={18} className="text-yellow-400" />
              <span className="text-gray-400 text-sm">{el ? 'Ενεργά Badges' : 'Active Badges'}</span>
            </div>
            <p className="text-2xl font-bold text-white">{gamStats.active_badges ?? badges.length}</p>
          </div>
          <div className="bg-misel-card border border-white/10 rounded-sm p-5">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp size={18} className="text-primary" />
              <span className="text-gray-400 text-sm">{el ? 'Πόντοι' : 'Total Points'}</span>
            </div>
            <p className="text-2xl font-bold text-white">{gamStats.total_points_awarded || 0}</p>
          </div>
          <div className="bg-misel-card border border-white/10 rounded-sm p-5">
            <div className="flex items-center gap-3 mb-2">
              <Users size={18} className="text-green-400" />
              <span className="text-gray-400 text-sm">{el ? 'Παίκτες' : 'Active Players'}</span>
            </div>
            <p className="text-2xl font-bold text-white">{gamStats.active_gamification_users || 0}</p>
          </div>
          <div className="bg-misel-card border border-white/10 rounded-sm p-5">
            <div className="flex items-center gap-3 mb-2">
              <BarChart3 size={18} className="text-blue-400" />
              <span className="text-gray-400 text-sm">{el ? 'Ολοκληρώσεις' : 'Completed Videos'}</span>
            </div>
            <p className="text-2xl font-bold text-white">{gamStats.total_completed_videos || 0}</p>
          </div>
        </div>
      )}

      {showForm && (
        <form onSubmit={saveBadge} className="bg-misel-card border border-white/10 rounded-sm p-6 mb-8 space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="text-white font-bold text-lg">{editingId ? (el ? 'Επεξεργασία Badge' : 'Edit Badge') : (el ? 'Νέο Badge' : 'New Badge')}</h2>
            <Button type="button" variant="ghost" onClick={() => setShowForm(false)} className="text-gray-400">
              <X size={16} />
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input className={adminInputClass} placeholder={el ? 'Τίτλος EL' : 'Greek title'} value={form.name_el} onChange={(e) => setForm({ ...form, name_el: e.target.value })} />
            <Input className={adminInputClass} placeholder={el ? 'Τίτλος EN' : 'English title'} value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} />
            <textarea className={adminTextareaClass} placeholder={el ? 'Περιγραφή EL' : 'Greek description'} value={form.description_el} onChange={(e) => setForm({ ...form, description_el: e.target.value })} />
            <textarea className={adminTextareaClass} placeholder={el ? 'Περιγραφή EN' : 'English description'} value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} />
            <Input className={adminInputClass} placeholder="slug (optional)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
            <Input className={adminInputClass} placeholder="icon, e.g. award" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} />
            <Input className={adminInputClass} type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
            <Select value={form.is_active ? 'active' : 'inactive'} onValueChange={(value) => setForm({ ...form, is_active: value === 'active' })}>
              <SelectTrigger className={adminSelectTriggerClass}><SelectValue /></SelectTrigger>
              <SelectContent className={adminSelectContentClass}>
                <SelectItem className={adminSelectItemClass} value="active">{el ? 'Ενεργό' : 'Active'}</SelectItem>
                <SelectItem className={adminSelectItemClass} value="inactive">{el ? 'Ανενεργό' : 'Inactive'}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Select value={form.trigger} onValueChange={(value) => setForm({ ...form, trigger: value })}>
              <SelectTrigger className={adminSelectTriggerClass}><SelectValue placeholder="Trigger" /></SelectTrigger>
              <SelectContent className={adminSelectContentClass}>
                {TRIGGERS.map((trigger) => <SelectItem className={adminSelectItemClass} key={trigger} value={trigger}>{trigger}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input className={adminInputClass} type="number" step="1" placeholder={el ? 'Τιμή trigger' : 'Trigger value'} value={form.trigger_value} onChange={(e) => setForm({ ...form, trigger_value: e.target.value })} disabled={form.trigger === 'manual'} />
            <Select value={form.reward_type} onValueChange={(value) => setForm({ ...form, reward_type: value })}>
              <SelectTrigger className={adminSelectTriggerClass}><SelectValue placeholder="Reward" /></SelectTrigger>
              <SelectContent className={adminSelectContentClass}>
                {REWARD_TYPES.map((reward) => <SelectItem className={adminSelectItemClass} key={reward} value={reward}>{reward}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input className={adminInputClass} type="number" step="0.01" placeholder={el ? 'Αξία ανταμοιβής' : 'Reward value'} value={form.reward_value} onChange={(e) => setForm({ ...form, reward_value: e.target.value })} />
            <Input className={adminInputClass} type="number" step="1" placeholder={el ? 'Πόντοι bonus' : 'Points reward'} value={form.points_reward} onChange={(e) => setForm({ ...form, points_reward: e.target.value })} />
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={saving} className="bg-primary hover:bg-primary/90">
              <Save size={16} className="mr-2" />
              {saving ? (el ? 'Αποθήκευση...' : 'Saving...') : (el ? 'Αποθήκευση' : 'Save Badge')}
            </Button>
          </div>
        </form>
      )}

      {gamStats?.badge_distribution?.length > 0 && (
        <div className="bg-misel-card border border-white/10 rounded-sm p-6 mb-8">
          <h2 className="text-white font-medium mb-4 flex items-center gap-2">
            <BarChart3 size={18} className="text-primary" />
            {el ? 'Κατανομή Badges' : 'Badge Distribution'}
          </h2>
          <div className="space-y-3">
            {gamStats.badge_distribution.map((bd) => {
              const maxCount = Math.max(...gamStats.badge_distribution.map((x) => x.earned_count), 1);
              return (
                <div key={bd.slug} className="flex items-center gap-3">
                  <span className="text-gray-400 text-sm w-40 truncate">{el ? bd.name_el : bd.name_en}</span>
                  <div className="flex-1 h-3 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full" style={{ width: `${(bd.earned_count / maxCount) * 100}%` }} />
                  </div>
                  <span className="text-white text-sm font-mono w-8 text-right">{bd.earned_count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {badges.map((badge) => (
          <div key={badge.id} className="bg-misel-card border border-white/10 rounded-sm p-4 flex flex-col lg:flex-row lg:items-center justify-between gap-4" data-testid={`admin-badge-${badge.slug}`}>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${badge.color || '#b91c1c'}20` }}>
                <Award size={18} style={{ color: badge.color || '#b91c1c' }} />
              </div>
              <div>
                <p className="text-white font-medium">{el ? badge.name_el || badge.name : badge.name_en || badge.name}</p>
                <p className="text-xs text-gray-500">
                  {badge.trigger}: {badge.trigger_value ?? '-'}
                  {badge.reward_type && badge.reward_type !== 'none' && ` · ${el ? 'Ανταμοιβή' : 'Reward'}: ${badge.reward_type} ${badge.reward_value ?? badge.points_reward ?? ''}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => toggleBadge(badge.id, badge.is_active)} data-testid={`toggle-badge-${badge.slug}`}
                className={`relative w-12 h-6 rounded-full transition-colors ${badge.is_active ? 'bg-green-500' : 'bg-gray-600'}`}>
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${badge.is_active ? 'left-7' : 'left-1'}`} />
              </button>
              <Button variant="ghost" size="icon" onClick={() => openEdit(badge)} className="text-gray-400 hover:text-white">
                <Edit size={16} />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => archiveBadge(badge.id)} className="text-gray-400 hover:text-red-400">
                <Trash2 size={16} />
              </Button>
            </div>
          </div>
        ))}
        {badges.length === 0 && (
          <div className="bg-misel-card border border-white/10 rounded-sm p-8 text-center text-gray-500">
            {el ? 'Δεν υπάρχουν badges ακόμα.' : 'No badges yet.'}
          </div>
        )}
      </div>
    </div>
  );
};

export default BadgesManagement;
